import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Plus, FloppyDisk, Drop, Sparkle } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'
import { callBreeamAnalyzer, parseAiJson } from '../lib/breeamAnalyzer'

// El botón CAL AG1 (descarga de la Calculadora oficial rellena) vive ahora en
// App.js, en la fila estándar de botones del criterio, para que esté en el
// mismo sitio que CAL CONT1, CAL MAT3, CAL MAT1 y CAL SyB2.

async function saveDataToSupabase(projectId, data) {
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr || !user) return
    const { error } = await supabase.from('evaluations').upsert({
      project_id: projectId,
      user_id: user.id,
      category: 'Agua',
      criteria_code: 'AG1-DATA',
      criteria_name: 'AG1 Datos',
      points_obtained: 0, points_max: 0, cumple: false,
      justificacion: JSON.stringify(data),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id,category,criteria_code' })
    if (error) console.error('saveData error:', error.message)
  } catch (e) { console.error('saveData exception:', e.message) }
}

async function loadDataFromSupabase(projectId) {
  try {
    const { data } = await supabase.from('evaluations')
      .select('justificacion').eq('project_id', projectId)
      .eq('criteria_code', 'AG1-DATA').single()
    if (data?.justificacion) return JSON.parse(data.justificacion)
  } catch (e) {}
  return null
}

const BUILDING_WEIGHTS = {
  'Hotel': {
    wc: 0.1771, urinal: 0.0171, basin: 0.0751, shower: 0.2047,
    bath: 0.1754, kitchenStaff: 0.0232, domWashing: null, domDishwasher: 0.0017,
    preRinse: 0.0241, wasteDisposal: 0.0199, commDishwasher: 0.0399, commWashing: 0.2428,
  },
  'Otras tipologías': {
    wc: 0.2760, urinal: 0.0414, basin: 0.1785, shower: 0.2221,
    bath: null, kitchenStaff: 0.1001, domWashing: null, domDishwasher: 0.0125,
    preRinse: 0.1137, wasteDisposal: null, commDishwasher: 0.0142, commWashing: null,
  },
}

const TABLA_29 = {
  'Ref': [0, 1, 2], '1': [1, 2, 3], '2': [2, 3, 4],
  '3': [3, 4, 5],  '4': [3, 4, 5], '5': [4, 5, 5],
}

const FIXTURE_LABELS = {
  inodoros:       { label: 'Inodoros (WC)',                   wKey: 'wc' },
  urinarios:      { label: 'Urinarios',                       wKey: 'urinal' },
  grifos:         { label: 'Grifos lavabo',                   wKey: 'basin' },
  duchas:         { label: 'Duchas',                          wKey: 'shower' },
  baneras:        { label: 'Bañeras',                         wKey: 'bath' },
  grifosCocina:   { label: 'Grifos cocina personal',          wKey: 'kitchenStaff' },
  lavavajillas:   { label: 'Lavavajillas doméstico',          wKey: 'domDishwasher' },
  preRinse:       { label: 'Grifo preaclarado restaurante',   wKey: 'preRinse' },
  commDishwasher: { label: 'Lavavajillas comercial',          wKey: 'commDishwasher' },
  commWashing:    { label: 'Lavadora comercial',              wKey: 'commWashing' },
}

const LEVELS = ['Referencia', '1', '2', '3', '4', '5']
const BUILDING_TYPES = Object.keys(BUILDING_WEIGHTS)

function newFixture(id)  { return { id, descripcion: '', valor: '', cantidad: '' } }
function newCisterna(id) { return { id, descripcion: '', capacidad: '', frecuencia: '', cantidad: '' } }
function newPresion(id)  { return { id, descripcion: '', volumen: '', cantidad: '' } }

function calcMedia(items, valField = 'valor') {
  let tv = 0, tc = 0
  for (const f of items) {
    const v = parseFloat(f[valField]), c = parseFloat(f.cantidad)
    if (v > 0 && c > 0) { tv += v * c; tc += c }
  }
  return tc ? { media: tv / tc, totalCantidad: tc, totalValor: tv } : null
}

// ── Estilos constantes a nivel módulo
const INP = {
  padding: '0.3rem 0.45rem', fontSize: '0.72rem',
  border: '1px solid #B8CED8', borderRadius: '4px',
  background: 'white', fontFamily: "'IBM Plex Mono', monospace",
  boxSizing: 'border-box', color: '#1A2A35', width: '100%',
}
const INP_NARROW = { ...INP, border: '1px solid #3A7A9A', width: 'auto' }
const LBL = {
  fontSize: '0.58rem', fontWeight: '700', color: '#3A6A8A',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  display: 'block', marginBottom: '0.25rem',
}
const SEC_HDR = {
  background: '#2A4A5E', padding: '0.45rem 0.875rem',
  fontSize: '0.62rem', fontWeight: '700', color: '#9ECAE8',
  textTransform: 'uppercase', letterSpacing: '0.07em',
  borderBottom: '1px solid #3A6A8A44',
}
const CHIP_BASE = { borderRadius: '5px', padding: '0.4rem 0.75rem', minWidth: '130px' }

// ── Sub-componentes con React.memo para evitar re-renders que roban el foco

function ResultChip({ label, value, unit, highlight }) {
  return (
    <div style={{ ...CHIP_BASE, background: highlight ? '#2A4A5E' : '#EBF3F8', border: `1px solid ${highlight ? '#2A4A5E' : '#9ECAE8'}` }}>
      <div style={{ fontSize: '0.55rem', fontWeight: '700', color: highlight ? '#9ECAE8' : '#3A6A8A', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.15rem' }}>{label}</div>
      <div style={{ fontSize: '1.15rem', fontWeight: '800', color: highlight ? 'white' : '#1A3040', fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>
        {value} <span style={{ fontSize: '0.65rem', fontWeight: '400', color: highlight ? '#9ECAE8' : '#5A8AA0' }}>{unit}</span>
      </div>
    </div>
  )
}

function ResultRow({ result, label, proporcional, factor = 0.7 }) {
  if (!result) return <div style={{ fontSize: '0.65rem', color: '#9AB0BE', fontStyle: 'italic' }}>Sin datos suficientes</div>
  return (
    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
      <ResultChip label={`${label} — total`} value={result.totalCantidad} unit="uds." />
      <ResultChip label="Caudal efectivo medio" value={result.media.toFixed(3)} unit="L" highlight />
      {proporcional && <ResultChip label={`Proporcional (×${factor})`} value={(result.media * factor).toFixed(3)} unit="L" />}
    </div>
  )
}

function AddBtn({ onClick, label }) {
  return (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: 'none', border: '1px dashed #6AAAC8', borderRadius: '4px', padding: '0.3rem 0.6rem', cursor: 'pointer', color: '#2A6A8A', fontSize: '0.68rem', fontWeight: '600', marginTop: '0.4rem', fontFamily: "'DM Sans', sans-serif" }}>
      <Plus size={11} /> {label}
    </button>
  )
}

// FixtureRow envuelto en React.memo — no se re-monta al cambiar callbacks del padre
const FixtureRow = React.memo(function FixtureRow({ item, onUpdate, onRemove, canRemove, valuePlaceholder, valueLabel, valueUnit, showLabels }) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 3, minWidth: '160px' }}>
        {showLabels && <label style={LBL}>Descripción del aparato</label>}
        <input value={item.descripcion} onChange={e => onUpdate(item.id, 'descripcion', e.target.value)} placeholder="Marca / modelo / referencia" style={INP} />
      </div>
      <div style={{ flex: 1, minWidth: '90px' }}>
        {showLabels && <label style={LBL}>{valueLabel} ({valueUnit})</label>}
        <input type="number" min="0" step="0.01" value={item.valor} onChange={e => onUpdate(item.id, 'valor', e.target.value)} placeholder={valuePlaceholder} style={INP} />
      </div>
      <div style={{ flex: 1, minWidth: '70px' }}>
        {showLabels && <label style={LBL}>Cantidad</label>}
        <input type="number" min="0" step="1" value={item.cantidad} onChange={e => onUpdate(item.id, 'cantidad', e.target.value)} placeholder="uds." style={INP} />
      </div>
      {canRemove && (
        <button onClick={() => onRemove(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D4614A', fontSize: '1.1rem', paddingBottom: '0.2rem', alignSelf: 'flex-end' }}>×</button>
      )}
    </div>
  )
})

// CisternaRow también memoizado
const CisternaRow = React.memo(function CisternaRow({ item, onUpdate, onRemove, canRemove, showLabels }) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 3, minWidth: '130px' }}>
        {showLabels && <label style={LBL}>Descripción</label>}
        <input value={item.descripcion} onChange={e => onUpdate(item.id, 'descripcion', e.target.value)} placeholder="Tipo de cisterna" style={INP} />
      </div>
      <div style={{ flex: 1, minWidth: '70px' }}>
        {showLabels && <label style={LBL}>Capacidad (L)</label>}
        <input type="number" min="0" step="0.1" value={item.capacidad} onChange={e => onUpdate(item.id, 'capacidad', e.target.value)} placeholder="L" style={INP} />
      </div>
      <div style={{ flex: 1, minWidth: '80px' }}>
        {showLabels && <label style={LBL}>Frecuencia (desc/h)</label>}
        <input type="number" min="0" step="0.1" value={item.frecuencia} onChange={e => onUpdate(item.id, 'frecuencia', e.target.value)} placeholder="desc/h" style={INP} />
      </div>
      <div style={{ flex: 1, minWidth: '65px' }}>
        {showLabels && <label style={LBL}>Cantidad</label>}
        <input type="number" min="0" step="1" value={item.cantidad} onChange={e => onUpdate(item.id, 'cantidad', e.target.value)} placeholder="uds." style={INP} />
      </div>
      {canRemove && <button onClick={() => onRemove(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D4614A', fontSize: '1.1rem', paddingBottom: '0.2rem', alignSelf: 'flex-end' }}>×</button>}
    </div>
  )
})

const PresionRow = React.memo(function PresionRow({ item, onUpdate, onRemove, canRemove, showLabels }) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 3, minWidth: '130px' }}>
        {showLabels && <label style={LBL}>Descripción</label>}
        <input value={item.descripcion} onChange={e => onUpdate(item.id, 'descripcion', e.target.value)} placeholder="Tipo de válvula" style={INP} />
      </div>
      <div style={{ flex: 1, minWidth: '80px' }}>
        {showLabels && <label style={LBL}>Volumen descarga (L)</label>}
        <input type="number" min="0" step="0.1" value={item.volumen} onChange={e => onUpdate(item.id, 'volumen', e.target.value)} placeholder="L" style={INP} />
      </div>
      <div style={{ flex: 1, minWidth: '65px' }}>
        {showLabels && <label style={LBL}>Cantidad</label>}
        <input type="number" min="0" step="1" value={item.cantidad} onChange={e => onUpdate(item.id, 'cantidad', e.target.value)} placeholder="uds." style={INP} />
      </div>
      {canRemove && <button onClick={() => onRemove(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D4614A', fontSize: '1.1rem', paddingBottom: '0.2rem', alignSelf: 'flex-end' }}>×</button>}
    </div>
  )
})

// ── Componente principal
export default function AG1Calculator({ projectId, onSave, saving, sandbox = false }) {
  const storageKey = `ag1_${projectId}`
  const load = () => { try { return JSON.parse(localStorage.getItem(storageKey) || 'null') } catch { return null } }

  const [savedOk, setSavedOk] = useState(false)
  const [activeTab, setActiveTab] = useState('caudales')
  const [buildingType, setBuildingType] = useState(() => load()?.buildingType || 'Hotel')

  const [inodoros, setInodoros]         = useState(() => load()?.inodoros     || [newFixture(1)])
  const [nextInoId, setNextInoId]       = useState(() => load()?.nextInoId    || 2)
  const [urinCist, setUrinCist]         = useState(() => load()?.urinCist     || [newCisterna(1)])
  const [nextCistId, setNextCistId]     = useState(() => load()?.nextCistId   || 2)
  const [urinPres, setUrinPres]         = useState(() => load()?.urinPres     || [newPresion(1)])
  const [nextPresId, setNextPresId]     = useState(() => load()?.nextPresId   || 2)
  const [grifos, setGrifos]             = useState(() => load()?.grifos       || [newFixture(1)])
  const [nextGrifId, setNextGrifId]     = useState(() => load()?.nextGrifId   || 2)
  const [grifosCocina, setGrifosCocina] = useState(() => load()?.grifosCocina || [newFixture(1)])
  const [nextCocId, setNextCocId]       = useState(() => load()?.nextCocId    || 2)
  const [duchas, setDuchas]             = useState(() => load()?.duchas       || [newFixture(1)])
  const [nextDuchId, setNextDuchId]     = useState(() => load()?.nextDuchId   || 2)
  const [baneras, setBaneras]           = useState(() => load()?.baneras      || [newFixture(1)])
  const [nextBanId, setNextBanId]       = useState(() => load()?.nextBanId    || 2)

  const [niveles, setNiveles] = useState(() => load()?.niveles || {
    inodoros: 'Referencia', urinarios: 'Referencia', grifos: 'Referencia',
    duchas: 'Referencia', baneras: 'Referencia', grifosCocina: 'Referencia',
    lavavajillas: 'Referencia', preRinse: 'Referencia', commDishwasher: 'Referencia', commWashing: 'Referencia',
  })
  const [especificado, setEspecificado] = useState(() => load()?.especificado || {
    inodoros: true, urinarios: false, grifos: true,
    duchas: false, baneras: false, grifosCocina: false,
    lavavajillas: false, preRinse: false, commDishwasher: false, commWashing: false,
  })
  const [sistemaAguas, setSistemaAguas] = useState(() => load()?.sistemaAguas || 'Sin sistema')
  const [zonaPluviometrica, setZonaPluviometrica] = useState(() => load()?.zonaPluviometrica || 'Zona de precipitación 4')
  const [extrayendo, setExtrayendo] = useState(false)
  const [msgExtraccion, setMsgExtraccion] = useState(null)

  // ── Ref con estado actualizado síncronamente para persistencia en desmontaje
  const stateRef = useRef({})
  stateRef.current = {
    buildingType, inodoros, nextInoId, urinCist, nextCistId, urinPres, nextPresId,
    grifos, nextGrifId, grifosCocina, nextCocId, duchas, nextDuchId, baneras, nextBanId,
    niveles, especificado, sistemaAguas, zonaPluviometrica,
  }

  // BUG-010 fix 2026-05-28: race condition. El auto-save disparaba antes de que la
  // carga remota terminase, sobreescribiendo BD con localStorage vacío. Flag para
  // bloquear todas las escrituras (auto-save + desmontaje) hasta que el load termine.
  const remoteLoadedRef = useRef(false)

  // Cargar desde Supabase al montar
  useEffect(() => {
    if (!projectId) return
    remoteLoadedRef.current = false
    loadDataFromSupabase(projectId).then(remote => {
      if (remote) {
        if (remote.buildingType) setBuildingType(remote.buildingType)
        if (remote.inodoros) setInodoros(remote.inodoros)
        if (remote.nextInoId) setNextInoId(remote.nextInoId)
        if (remote.urinCist) setUrinCist(remote.urinCist)
        if (remote.nextCistId) setNextCistId(remote.nextCistId)
        if (remote.urinPres) setUrinPres(remote.urinPres)
        if (remote.nextPresId) setNextPresId(remote.nextPresId)
        if (remote.grifos) setGrifos(remote.grifos)
        if (remote.nextGrifId) setNextGrifId(remote.nextGrifId)
        if (remote.grifosCocina) setGrifosCocina(remote.grifosCocina)
        if (remote.nextCocId) setNextCocId(remote.nextCocId)
        if (remote.duchas) setDuchas(remote.duchas)
        if (remote.nextDuchId) setNextDuchId(remote.nextDuchId)
        if (remote.baneras) setBaneras(remote.baneras)
        if (remote.nextBanId) setNextBanId(remote.nextBanId)
        if (remote.niveles) setNiveles(remote.niveles)
        if (remote.especificado) setEspecificado(remote.especificado)
        if (remote.sistemaAguas) setSistemaAguas(remote.sistemaAguas)
        if (remote.zonaPluviometrica) setZonaPluviometrica(remote.zonaPluviometrica)
      }
    }).finally(() => { remoteLoadedRef.current = true })
  }, [projectId]) // eslint-disable-line

  function saveNow() {
    const data = stateRef.current
    localStorage.setItem(storageKey, JSON.stringify(data))
    if (!sandbox) saveDataToSupabase(projectId, data)
    setSavedOk(true)
    setTimeout(() => setSavedOk(false), 2000)
  }

  // Persistir en cada cambio — localStorage inmediato + Supabase con debounce
  // (BUG-009 fix 2026-05-20: antes solo se guardaba a localStorage. Al cerrar/reabrir
  // el criterio, useEffect cargaba de Supabase (versión vieja) y sobrescribía los
  // cambios no sincronizados. Ahora el auto-save sincroniza a BD tras 1.5s sin cambios.)
  const supabaseSaveTimer = useRef(null)
  useEffect(() => {
    if (!projectId) return
    if (!remoteLoadedRef.current) return // BUG-010: no escribir hasta tener datos del remote
    const data = stateRef.current
    localStorage.setItem(storageKey, JSON.stringify(data))
    // Debounce 1500ms para no spammear Supabase con cada keystroke
    if (supabaseSaveTimer.current) clearTimeout(supabaseSaveTimer.current)
    if (!sandbox) {
      supabaseSaveTimer.current = setTimeout(() => {
        saveDataToSupabase(projectId, data)
      }, 1500)
    }
    return () => { if (supabaseSaveTimer.current) clearTimeout(supabaseSaveTimer.current) }
    // storageKey se deriva de projectId (ya en deps) — no lo añadimos para
    // no cambiar la semántica del debounce ante renders sin cambio real.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingType, inodoros, nextInoId, urinCist, nextCistId, urinPres, nextPresId,
      grifos, nextGrifId, grifosCocina, nextCocId, duchas, nextDuchId, baneras, nextBanId,
      niveles, especificado, sistemaAguas, zonaPluviometrica, projectId])

  // Persistir al desmontar (garantía ante navegación rápida) — flushea a Supabase también
  useEffect(() => {
    return () => {
      if (!projectId) return
      if (!remoteLoadedRef.current) return // BUG-010: no escribir hasta tener datos del remote
      const data = stateRef.current
      localStorage.setItem(`ag1_${projectId}`, JSON.stringify(data))
      // Cancelar debounce pendiente y forzar guardado síncrono (salvo en sandbox)
      if (supabaseSaveTimer.current) {
        clearTimeout(supabaseSaveTimer.current)
        if (!sandbox) saveDataToSupabase(projectId, data)
      }
    }
  }, [projectId]) // eslint-disable-line

  // ── Callbacks estables con useCallback — evitan que FixtureRow reciba nuevas
  //    referencias de función en cada render, lo que causaba pérdida de foco
  const updIno  = useCallback((id, f, v) => setInodoros(p  => p.map(x => x.id === id ? { ...x, [f]: v } : x)), [])
  const rmIno   = useCallback((id)        => setInodoros(p  => p.length > 1 ? p.filter(x => x.id !== id) : p), [])
  const updCist = useCallback((id, f, v) => setUrinCist(p  => p.map(x => x.id === id ? { ...x, [f]: v } : x)), [])
  const rmCist  = useCallback((id)        => setUrinCist(p  => p.length > 1 ? p.filter(x => x.id !== id) : p), [])
  const updPres = useCallback((id, f, v) => setUrinPres(p  => p.map(x => x.id === id ? { ...x, [f]: v } : x)), [])
  const rmPres  = useCallback((id)        => setUrinPres(p  => p.length > 1 ? p.filter(x => x.id !== id) : p), [])
  const updGrif = useCallback((id, f, v) => setGrifos(p    => p.map(x => x.id === id ? { ...x, [f]: v } : x)), [])
  const rmGrif  = useCallback((id)        => setGrifos(p    => p.length > 1 ? p.filter(x => x.id !== id) : p), [])
  const updCoc  = useCallback((id, f, v) => setGrifosCocina(p => p.map(x => x.id === id ? { ...x, [f]: v } : x)), [])
  const rmCoc   = useCallback((id)        => setGrifosCocina(p => p.length > 1 ? p.filter(x => x.id !== id) : p), [])
  const updDuch = useCallback((id, f, v) => setDuchas(p    => p.map(x => x.id === id ? { ...x, [f]: v } : x)), [])
  const rmDuch  = useCallback((id)        => setDuchas(p    => p.length > 1 ? p.filter(x => x.id !== id) : p), [])
  const updBan  = useCallback((id, f, v) => setBaneras(p   => p.map(x => x.id === id ? { ...x, [f]: v } : x)), [])
  const rmBan   = useCallback((id)        => setBaneras(p   => p.length > 1 ? p.filter(x => x.id !== id) : p), [])

  // ── Cálculos
  const mediaIno  = calcMedia(inodoros)
  const mediaGrif = calcMedia(grifos)
  const mediaCoc  = calcMedia(grifosCocina)
  const mediaDuch = calcMedia(duchas)
  const mediaBan  = calcMedia(baneras)
  const cistItems = urinCist.map(u => ({ ...u, valor: (parseFloat(u.capacidad) || 0) * (parseFloat(u.frecuencia) || 0) }))
  const mediaCist = calcMedia(cistItems)
  const mediaPres = calcMedia(urinPres, 'volumen')

  // ── Puntuación
  const { nivelGlobal, pts, levelKey } = (() => {
    const weights = BUILDING_WEIGHTS[buildingType] || BUILDING_WEIGHTS['Hotel']
    let totalW = 0, weightedLvl = 0
    for (const [key, { wKey }] of Object.entries(FIXTURE_LABELS)) {
      if (!especificado[key]) continue
      const rawW = weights[wKey]
      if (rawW == null) continue
      totalW += rawW
      weightedLvl += rawW * (niveles[key] === 'Referencia' ? 0 : (parseInt(niveles[key]) || 0))
    }
    if (!totalW) return { nivelGlobal: 0, pts: 0, levelKey: 'Ref' }
    const ng = weightedLvl / totalW
    const lk = Math.floor(ng) <= 0 ? 'Ref' : String(Math.min(5, Math.floor(ng)))
    const si = sistemaAguas === 'Sin sistema' ? 0 : sistemaAguas === 'Nivel 4 (85-95%)' ? 1 : 2
    return { nivelGlobal: ng, pts: (TABLA_29[lk] || [0, 1, 2])[si], levelKey: lk }
  })()

  // ────────────────────────────────────────────────────────────────────────
  // AUTOFILL con IA · lee las evidencias del criterio AG 1 (certificados
  // de suministro, fichas técnicas de sanitarios) y rellena las tablas de
  // aparatos automáticamente. El usuario revisa después.
  // ────────────────────────────────────────────────────────────────────────
  const autoRellenar = async () => {
    if (!projectId) return
    setExtrayendo(true); setMsgExtraccion(null)
    try {
      // 1. Encontrar la evaluación AG 1 del proyecto y sus evidencias
      const { data: evalRow } = await supabase
        .from('evaluations')
        .select('id')
        .eq('project_id', projectId).eq('criteria_code', 'AG 1').maybeSingle()
      if (!evalRow?.id) throw new Error('Aún no hay evaluación AG 1 · sube antes evidencias desde el módulo Criterios BREEAM.')
      const { data: evs } = await supabase
        .from('evidences')
        .select('file_name, file_path, storage_bucket')
        .eq('evaluation_id', evalRow.id)
      if (!evs?.length) throw new Error('No hay evidencias en AG 1. Sube certificado de suministro o fichas técnicas primero.')

      // 2. Firmar URLs (max 12 documentos para no saturar Claude)
      const docs = evs.slice(0, 12)
      const file_infos = []
      for (const e of docs) {
        const { data: sig } = await supabase.storage.from(e.storage_bucket || 'evidences').createSignedUrl(e.file_path, 1200)
        if (sig?.signedUrl) file_infos.push({ name: e.file_name, signed_url: sig.signedUrl, media_type: 'application/pdf' })
      }
      if (!file_infos.length) throw new Error('No se pudieron firmar URLs de las evidencias.')

      // 3. Prompt específico para extraer aparatos
      const prompt_text = `Eres un asesor BREEAM ES. Analiza los siguientes documentos (certificado de suministro, fichas técnicas de sanitarios) para EXTRAER TODOS LOS APARATOS SANITARIOS instalados en el edificio y sus CAUDALES o VOLÚMENES de descarga.

Devuelve SOLO un JSON con esta estructura exacta (sin comentarios ni markdown):
{
  "inodoros":       [ { "descripcion": "marca modelo", "valor": 4.5, "cantidad": 39 } ],
  "urinCist":       [ { "descripcion": "marca modelo", "capacidad": 1.5, "frecuencia": 4, "cantidad": 1 } ],
  "urinPres":       [ { "descripcion": "marca modelo", "volumen": 1.0, "cantidad": 1 } ],
  "grifos":         [ { "descripcion": "marca modelo Lavabo", "valor": 5.0, "cantidad": 78 } ],
  "grifosCocina":   [ { "descripcion": "marca modelo cocina", "valor": 6.0, "cantidad": 2 } ],
  "duchas":         [ { "descripcion": "marca modelo teleducha", "valor": 8.0, "cantidad": 40 } ],
  "baneras":        [ { "descripcion": "marca modelo bañera", "valor": 150, "cantidad": 6 } ],
  "buildingType":   "Hotel" | "Otras tipologías",
  "notas": "Comentario breve · fuentes usadas · cualquier caudal que no se pudo determinar"
}

REGLAS:
1. Unidades: inodoros L/descarga · urinarios cisterna L/hora capacidad + frecuencia veces/hora · urinarios presión L/descarga · grifos L/min · duchas L/min · bañeras L/uso.
2. Si un caudal no está explícito en las fichas, usa VALORES TÍPICOS del fabricante para ese modelo (p.ej. inodoro Duravit Architec suspendido con Geberit Sigma → 4.5 L o 3/6 L doble descarga → 4.5 L media, grifo Hansgrohe Starck 100 CoolStart → 5 L/min, teleducha Croma Select E EcoSmart → 9 L/min).
3. AGRUPA por modelo idéntico: si hay 39 unidades del mismo inodoro, una sola fila con cantidad 39.
4. Si un aparato no se encuentra en los documentos, deja el array vacío [].
5. Si es un edificio hotelero/turístico usa "Hotel". Si es otra tipología usa "Otras tipologías".
6. Los MEZCLADORES DE DUCHA/BAÑO se cuentan como "duchas" con su caudal L/min.
7. Los MEZCLADORES DE LAVABO (Starck, Focus, Finoris, etc.) se cuentan como "grifos" del lavabo.`

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
      const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim()
      const functionUrl = `${supabaseUrl}/functions/v1/breeam-analyzer`

      const apiData = await callBreeamAnalyzer(functionUrl, anonKey, {
        file_infos, direct_files: [], prompt_text, model: 'claude-sonnet-4-6', max_tokens: 4000,
      })
      const text = apiData?.content?.[0]?.text || ''
      const data = parseAiJson(text)

      // 4. Aplicar al estado
      const toRows = (arr, extra = {}) => (arr || []).map((r, i) => ({
        id: i + 1,
        descripcion: r.descripcion || '',
        cantidad: r.cantidad != null ? String(r.cantidad) : '',
        ...(extra),
        ...(r.valor != null ? { valor: String(r.valor) } : {}),
        ...(r.capacidad != null ? { capacidad: String(r.capacidad) } : {}),
        ...(r.frecuencia != null ? { frecuencia: String(r.frecuencia) } : {}),
        ...(r.volumen != null ? { volumen: String(r.volumen) } : {}),
      }))

      if (data.buildingType && BUILDING_TYPES.includes(data.buildingType)) setBuildingType(data.buildingType)
      if (data.inodoros?.length)     { setInodoros(toRows(data.inodoros)); setNextInoId(data.inodoros.length + 1) }
      if (data.urinCist?.length)     { setUrinCist(toRows(data.urinCist)); setNextCistId(data.urinCist.length + 1) }
      if (data.urinPres?.length)     { setUrinPres(toRows(data.urinPres)); setNextPresId(data.urinPres.length + 1) }
      if (data.grifos?.length)       { setGrifos(toRows(data.grifos)); setNextGrifId(data.grifos.length + 1) }
      if (data.grifosCocina?.length) { setGrifosCocina(toRows(data.grifosCocina)); setNextCocId(data.grifosCocina.length + 1) }
      if (data.duchas?.length)       { setDuchas(toRows(data.duchas)); setNextDuchId(data.duchas.length + 1) }
      if (data.baneras?.length)      { setBaneras(toRows(data.baneras)); setNextBanId(data.baneras.length + 1) }

      // 5. Calcular automáticamente niveles (Tabla 30 BREEAM ES) y "especificado"
      // según los caudales medios ponderados. Así la pestaña Puntuación queda
      // rellenada sin intervención manual.
      const mediaOf = (arr, field = 'valor') => {
        let tv = 0, tc = 0
        for (const it of (arr || [])) {
          const v = parseFloat(it[field]); const c = parseFloat(it.cantidad)
          if (v > 0 && c > 0) { tv += v * c; tc += c }
        }
        return tc ? (tv / tc) : null
      }
      const nivelBy = (m, umbrales) => {
        if (m == null) return 'Referencia'
        for (const [lvl, thr] of umbrales) { if (m <= thr) return lvl }
        return 'Referencia'
      }
      // Umbrales Tabla 30 BREEAM ES NC 2015 (mayor a menor consumo)
      const UMB = {
        inodoros:     [['5', 3.5], ['4', 4.0], ['3', 4.5], ['2', 5.0], ['1', 6.0]],
        grifos:       [['5', 2.0], ['4', 3.0], ['3', 4.0], ['2', 5.0], ['1', 6.0]],
        grifosCocina: [['5', 4.0], ['4', 5.0], ['3', 6.0], ['2', 8.0], ['1', 10.0]],
        duchas:       [['5', 6.0], ['4', 7.0], ['3', 8.0], ['2', 10.0], ['1', 12.0]],
        baneras:      [['5', 90], ['4', 95], ['3', 110], ['2', 120], ['1', 140]],
        urinarios:    [['5', 1.0], ['4', 1.5], ['3', 3.0], ['2', 4.0], ['1', 6.0]],
      }
      const hay = (arr) => (arr || []).some(it => parseFloat(it.cantidad) > 0)
      const mUri = mediaOf(data.urinPres, 'volumen')  // Para urinarios usamos presión (l/desc)

      const nuevosNiveles = {
        inodoros:     nivelBy(mediaOf(data.inodoros), UMB.inodoros),
        urinarios:    nivelBy(mUri, UMB.urinarios),
        grifos:       nivelBy(mediaOf(data.grifos), UMB.grifos),
        grifosCocina: nivelBy(mediaOf(data.grifosCocina), UMB.grifosCocina),
        duchas:       nivelBy(mediaOf(data.duchas), UMB.duchas),
        baneras:      nivelBy(mediaOf(data.baneras), UMB.baneras),
        lavavajillas: 'Referencia', preRinse: 'Referencia',
        commDishwasher: 'Referencia', commWashing: 'Referencia',
      }
      const nuevosEsp = {
        inodoros:     hay(data.inodoros),
        urinarios:    hay(data.urinCist) || hay(data.urinPres),
        grifos:       hay(data.grifos),
        grifosCocina: hay(data.grifosCocina),
        duchas:       hay(data.duchas),
        baneras:      hay(data.baneras),
        lavavajillas: false, preRinse: false, commDishwasher: false, commWashing: false,
      }
      setNiveles(nuevosNiveles)
      setEspecificado(nuevosEsp)

      const n = (data.inodoros?.length||0) + (data.urinCist?.length||0) + (data.urinPres?.length||0) +
                (data.grifos?.length||0) + (data.grifosCocina?.length||0) + (data.duchas?.length||0) + (data.baneras?.length||0)
      setMsgExtraccion({ tipo: 'ok', texto: `✓ ${n} tipos de aparato rellenados + niveles Tabla 30 calculados. Revisa la pestaña Puntuación y guarda.${data.notas ? ' · ' + data.notas : ''}` })
    } catch (e) {
      setMsgExtraccion({ tipo: 'error', texto: e.message || String(e) })
    } finally {
      setExtrayendo(false)
    }
  }

  return (
    <div style={{ marginBottom: '1.25rem', border: '2px solid #3A7A9A', borderRadius: '6px', overflow: 'hidden' }}>

      {/* Banner modo simulador (sandbox) */}
      {sandbox && (
        <div style={{ background: '#FFF4E0', borderBottom: '2px solid #C98A2E', padding: '0.5rem 0.9rem', fontSize: '0.72rem', color: '#8B5A1E', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem' }}>⚙️</span>
          <span>MODO SIMULADOR · tus cambios NO afectan al dato oficial del expediente BREEAM. Úsala para probar combinaciones de aparatos sanitarios y ver cómo influye en el nivel AG 1.</span>
        </div>
      )}

      {/* Cabecera */}
      <div style={{ background: '#1A3A4E', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <Drop size={15} weight="fill" style={{ color: '#7ABCD8', flexShrink: 0 }} />
        <span style={{ fontSize: '0.65rem', fontWeight: '700', color: '#7ABCD8', textTransform: 'uppercase', letterSpacing: '0.07em', flex: 1 }}>
          Calculadora BREEAM ES AG 1 — Consumo de agua: Caudal medio de aparatos sanitarios{sandbox ? ' · SIMULADOR' : ''}
        </span>
        {!sandbox && (
          <button onClick={autoRellenar} disabled={extrayendo}
            title="Analiza los documentos subidos como evidencia (Cert. Suministro, fichas técnicas) y rellena las tablas automáticamente"
            style={{
              background: extrayendo ? '#3A7A9A' : '#8D9A2E', border: 'none', borderRadius: 4,
              padding: '0.28rem 0.65rem', cursor: extrayendo ? 'wait' : 'pointer',
              color: 'white', fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.05em', fontFamily: "'DM Sans', sans-serif",
              display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            }}>
            <Sparkle size={11} weight="fill" />
            {extrayendo ? 'Extrayendo…' : 'Auto-rellenar con IA'}
          </button>
        )}
        <div style={{ display: 'flex', gap: '0.3rem' }}>
          {['caudales', 'puntuacion'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ background: activeTab === tab ? '#3A7A9A' : 'transparent', border: '1px solid #3A7A9A', borderRadius: '4px', padding: '0.25rem 0.6rem', cursor: 'pointer', color: activeTab === tab ? 'white' : '#7ABCD8', fontSize: '0.6rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'DM Sans', sans-serif" }}>
              {tab === 'caudales' ? 'Caudales' : 'Puntuación'}
            </button>
          ))}
        </div>
      </div>

      {msgExtraccion && (
        <div style={{
          padding: '0.55rem 0.9rem', fontSize: '0.72rem', fontFamily: "'DM Sans', sans-serif",
          background: msgExtraccion.tipo === 'ok' ? '#EFF5E1' : '#FFF1ED',
          color: msgExtraccion.tipo === 'ok' ? '#4A5A0F' : '#D4614A',
          borderBottom: '1px solid ' + (msgExtraccion.tipo === 'ok' ? '#8D9A2E' : '#D4614A'),
        }}>
          {msgExtraccion.texto}
        </div>
      )}

      {/* ── Tab Caudales */}
      {activeTab === 'caudales' && (
        <div style={{ background: '#F2F8FB' }}>

          <div style={{ padding: '0.75rem 0.875rem 0', display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={LBL}>Tipo de edificio</label>
              <select value={buildingType} onChange={e => setBuildingType(e.target.value)} style={INP_NARROW}>
                {BUILDING_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ fontSize: '0.6rem', color: '#5A8AA0', paddingBottom: '0.35rem' }}>
              Los caudales medios calculados aquí se introducen en la Calculadora BREEAM ES AG 1 principal
            </div>
          </div>

          {/* INODOROS */}
          <div style={{ borderTop: '1px solid #C5DDE8', marginTop: '0.75rem' }}>
            <div style={SEC_HDR}>Inodoros — Caudal efectivo de descarga (L)</div>
            <div style={{ padding: '0.75rem 0.875rem' }}>
              {inodoros.map((f, i) => (
                <FixtureRow key={f.id} item={f} showLabels={i === 0}
                  onUpdate={updIno} onRemove={rmIno} canRemove={inodoros.length > 1}
                  valueLabel="Caudal descarga" valueUnit="L" valuePlaceholder="ej. 3.5" />
              ))}
              {inodoros.length < 5 && <AddBtn onClick={() => { setInodoros(p => [...p, newFixture(nextInoId)]); setNextInoId(n => n + 1) }} label="Añadir tipo de inodoro" />}
              <ResultRow result={mediaIno} label="Inodoros" proporcional={false} />
              {mediaIno && <div style={{ fontSize: '0.6rem', color: '#5A8AA0', marginTop: '0.3rem', fontFamily: "'IBM Plex Mono', monospace" }}>Introduce <strong>{mediaIno.media.toFixed(3)} L</strong> como "Caudal efectivo de descarga medio" en la calculadora principal</div>}
            </div>
          </div>

          {/* URINARIOS */}
          <div style={{ borderTop: '1px solid #C5DDE8' }}>
            <div style={SEC_HDR}>Urinarios</div>
            <div style={{ padding: '0.75rem 0.875rem' }}>
              <div style={{ marginBottom: '0.875rem' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#3A6A8A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Cisternas automáticas</div>
                {urinCist.map((u, i) => (
                  <CisternaRow key={u.id} item={u} showLabels={i === 0}
                    onUpdate={updCist} onRemove={rmCist} canRemove={urinCist.length > 1} />
                ))}
                {urinCist.length < 5 && <AddBtn onClick={() => { setUrinCist(p => [...p, newCisterna(nextCistId)]); setNextCistId(n => n + 1) }} label="Añadir cisterna" />}
                {mediaCist && (
                  <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                    <ResultChip label="Total cisternas" value={mediaCist.totalCantidad} unit="uds." />
                    <ResultChip label="Capacidad media efectiva" value={(mediaCist.totalValor / mediaCist.totalCantidad).toFixed(3)} unit="L/h" highlight />
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#3A6A8A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Válvulas de presión</div>
                {urinPres.map((u, i) => (
                  <PresionRow key={u.id} item={u} showLabels={i === 0}
                    onUpdate={updPres} onRemove={rmPres} canRemove={urinPres.length > 1} />
                ))}
                {urinPres.length < 5 && <AddBtn onClick={() => { setUrinPres(p => [...p, newPresion(nextPresId)]); setNextPresId(n => n + 1) }} label="Añadir válvula" />}
                {mediaPres && (
                  <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                    <ResultChip label="Total válvulas" value={mediaPres.totalCantidad} unit="uds." />
                    <ResultChip label="Volumen descarga medio" value={mediaPres.media.toFixed(3)} unit="L" highlight />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* GRIFOS LAVABO */}
          <div style={{ borderTop: '1px solid #C5DDE8' }}>
            <div style={SEC_HDR}>Grifos de lavabo (excluidos cocina) — Caudal (L/min)</div>
            <div style={{ padding: '0.75rem 0.875rem' }}>
              {grifos.map((f, i) => (
                <FixtureRow key={f.id} item={f} showLabels={i === 0}
                  onUpdate={updGrif} onRemove={rmGrif} canRemove={grifos.length > 1}
                  valueLabel="Caudal" valueUnit="L/min" valuePlaceholder="ej. 5.0" />
              ))}
              {grifos.length < 5 && <AddBtn onClick={() => { setGrifos(p => [...p, newFixture(nextGrifId)]); setNextGrifId(n => n + 1) }} label="Añadir tipo de grifo" />}
              <ResultRow result={mediaGrif} label="Grifos lavabo" proporcional />
              {mediaGrif && <div style={{ fontSize: '0.6rem', color: '#5A8AA0', marginTop: '0.3rem', fontFamily: "'IBM Plex Mono', monospace" }}>Introduce <strong>{mediaGrif.media.toFixed(3)} L/min</strong> en la calculadora principal</div>}
            </div>
          </div>

          {/* GRIFOS COCINA */}
          <div style={{ borderTop: '1px solid #C5DDE8' }}>
            <div style={SEC_HDR}>Grifos de cocina — Caudal (L/min)</div>
            <div style={{ padding: '0.75rem 0.875rem' }}>
              {grifosCocina.map((f, i) => (
                <FixtureRow key={f.id} item={f} showLabels={i === 0}
                  onUpdate={updCoc} onRemove={rmCoc} canRemove={grifosCocina.length > 1}
                  valueLabel="Caudal" valueUnit="L/min" valuePlaceholder="ej. 9.0" />
              ))}
              {grifosCocina.length < 5 && <AddBtn onClick={() => { setGrifosCocina(p => [...p, newFixture(nextCocId)]); setNextCocId(n => n + 1) }} label="Añadir tipo de grifo" />}
              <ResultRow result={mediaCoc} label="Grifos cocina" proporcional />
            </div>
          </div>

          {/* DUCHAS */}
          <div style={{ borderTop: '1px solid #C5DDE8' }}>
            <div style={SEC_HDR}>Duchas — Caudal (L/min)</div>
            <div style={{ padding: '0.75rem 0.875rem' }}>
              {duchas.map((f, i) => (
                <FixtureRow key={f.id} item={f} showLabels={i === 0}
                  onUpdate={updDuch} onRemove={rmDuch} canRemove={duchas.length > 1}
                  valueLabel="Caudal" valueUnit="L/min" valuePlaceholder="ej. 8.0" />
              ))}
              {duchas.length < 5 && <AddBtn onClick={() => { setDuchas(p => [...p, newFixture(nextDuchId)]); setNextDuchId(n => n + 1) }} label="Añadir tipo de ducha" />}
              <ResultRow result={mediaDuch} label="Duchas" proporcional />
            </div>
          </div>

          {/* BAÑERAS */}
          <div style={{ borderTop: '1px solid #C5DDE8' }}>
            <div style={SEC_HDR}>Bañeras — Capacidad (L)</div>
            <div style={{ padding: '0.75rem 0.875rem' }}>
              {baneras.map((f, i) => (
                <FixtureRow key={f.id} item={f} showLabels={i === 0}
                  onUpdate={updBan} onRemove={rmBan} canRemove={baneras.length > 1}
                  valueLabel="Capacidad" valueUnit="L" valuePlaceholder="ej. 180" />
              ))}
              {baneras.length < 5 && <AddBtn onClick={() => { setBaneras(p => [...p, newFixture(nextBanId)]); setNextBanId(n => n + 1) }} label="Añadir tipo de bañera" />}
              <ResultRow result={mediaBan} label="Bañeras" proporcional factor={0.4} />
            </div>
          </div>

        </div>
      )}

      {/* ── Tab Puntuación */}
      {activeTab === 'puntuacion' && (
        <div style={{ background: '#F2F8FB', padding: '0.875rem' }}>

          <div style={{ fontSize: '0.65rem', color: '#3A6A8A', background: '#E0EFF7', border: '1px solid #9ECAE8', borderRadius: '5px', padding: '0.5rem 0.75rem', marginBottom: '0.875rem' }}>
            Consulta la Tabla 30 de BREEAM ES para determinar el nivel de eficiencia (1–5) de cada aparato según su caudal medio calculado. Marca solo los aparatos instalados en el edificio.
          </div>

          <div style={{ background: 'white', border: '1px solid #B8CED8', borderRadius: '6px', overflow: 'hidden', marginBottom: '0.875rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
              <thead>
                <tr style={{ background: '#2A4A5E' }}>
                  <th style={{ padding: '0.5rem 0.75rem', color: '#9ECAE8', fontWeight: '700', textAlign: 'left', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Aparato sanitario</th>
                  <th style={{ padding: '0.5rem 0.5rem', color: '#9ECAE8', fontWeight: '700', textAlign: 'center', fontSize: '0.6rem', textTransform: 'uppercase', width: '70px' }}>Especificado</th>
                  <th style={{ padding: '0.5rem 0.5rem', color: '#9ECAE8', fontWeight: '700', textAlign: 'center', fontSize: '0.6rem', textTransform: 'uppercase', width: '130px' }}>Nivel (Tabla 30)</th>
                  <th style={{ padding: '0.5rem 0.5rem', color: '#9ECAE8', fontWeight: '700', textAlign: 'center', fontSize: '0.6rem', textTransform: 'uppercase', width: '65px' }}>Peso</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(FIXTURE_LABELS).map(([key, { label, wKey }], i) => {
                  const weights = BUILDING_WEIGHTS[buildingType] || BUILDING_WEIGHTS['Hotel']
                  const rawW = weights[wKey]
                  const na = rawW == null
                  const isSpec = especificado[key]
                  return (
                    <tr key={key} style={{ borderTop: '1px solid #E0EFF7', background: i % 2 === 0 ? 'white' : '#F7FBFD', opacity: na ? 0.4 : 1 }}>
                      <td style={{ padding: '0.45rem 0.75rem', color: '#1A3040', fontWeight: isSpec ? '600' : '400' }}>{label}</td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'center' }}>
                        {na ? <span style={{ fontSize: '0.58rem', color: '#9AB0BE' }}>N/A</span>
                            : <input type="checkbox" checked={isSpec} onChange={e => setEspecificado(prev => ({ ...prev, [key]: e.target.checked }))} style={{ cursor: 'pointer' }} />}
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'center' }}>
                        {!na && isSpec
                          ? <select value={niveles[key]} onChange={e => setNiveles(prev => ({ ...prev, [key]: e.target.value }))} style={{ ...INP, fontSize: '0.68rem', padding: '0.25rem 0.35rem', border: '1px solid #3A7A9A', width: '120px' }}>
                              {LEVELS.map(l => <option key={l}>{l}</option>)}
                            </select>
                          : <span style={{ fontSize: '0.6rem', color: '#9AB0BE' }}>—</span>}
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.65rem', color: '#3A6A8A' }}>
                        {na ? '—' : (rawW * 100).toFixed(1) + '%'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div style={{ background: 'white', border: '1px solid #B8CED8', borderRadius: '6px', padding: '0.75rem', marginBottom: '0.875rem' }}>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <label style={LBL}>Zona de precipitación (Köppen)</label>
                <select value={zonaPluviometrica} onChange={e => setZonaPluviometrica(e.target.value)} style={INP_NARROW}>
                  <option>Zona de precipitación 1</option>
                  <option>Zona de precipitación 2</option>
                  <option>Zona de precipitación 3</option>
                  <option>Zona de precipitación 4</option>
                  <option>Zona de precipitación 5</option>
                </select>
                <div style={{ marginTop: '0.3rem', fontSize: '0.6rem', color: '#5A8AA0' }}>
                  Clasificación climática del emplazamiento (Tabla 31 BREEAM ES)
                </div>
              </div>
              <div>
                <label style={LBL}>Sistema de aguas grises / pluviales</label>
                <select value={sistemaAguas} onChange={e => setSistemaAguas(e.target.value)} style={INP_NARROW}>
                  <option>Sin sistema</option>
                  <option>Nivel 4 (85-95%)</option>
                  <option>Nivel 5 (&gt;95%)</option>
                </select>
                <div style={{ marginTop: '0.3rem', fontSize: '0.6rem', color: '#5A8AA0' }}>
                  % de la demanda cubierta por agua no potable reciclada
                </div>
              </div>
            </div>
          </div>

          <div style={{ background: 'white', border: '1px solid #B8CED8', borderRadius: '6px', padding: '0.875rem' }}>
            <div style={{ display: 'flex', gap: '0.875rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.875rem' }}>
              <div style={{ background: '#EBF3F8', border: '2px solid #2A4A5E', borderRadius: '6px', padding: '0.5rem 1.25rem', textAlign: 'center', minWidth: '130px' }}>
                <div style={{ fontSize: '0.55rem', fontWeight: '700', color: '#2A4A5E', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.2rem' }}>Nivel global (Tabla 30)</div>
                <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#1A3040', fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>{nivelGlobal.toFixed(2)}</div>
                <div style={{ fontSize: '0.58rem', color: '#5A8AA0', marginTop: '0.15rem' }}>→ Nivel {levelKey}</div>
              </div>
              <div style={{ background: '#1A3A4E', borderRadius: '6px', padding: '0.5rem 1.25rem', textAlign: 'center', minWidth: '100px' }}>
                <div style={{ fontSize: '0.55rem', fontWeight: '700', color: '#9ECAE8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.2rem' }}>Puntos AG 1</div>
                <div style={{ fontSize: '1.6rem', fontWeight: '800', color: 'white', fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>
                  {pts}<span style={{ fontSize: '0.8rem', color: '#7ABCD8' }}>/5</span>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontSize: '0.58rem', fontWeight: '700', color: '#3A6A8A', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Tabla 29 — Nivel {levelKey}</div>
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                  {[['Sin sistema', 0], ['Nivel 4 (85-95%)', 1], ['Nivel 5 (>95%)', 2]].map(([lbl, idx]) => {
                    const p = (TABLA_29[levelKey] || [0, 0, 0])[idx]
                    const active = sistemaAguas === lbl
                    return (
                      <div key={lbl} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.62rem', padding: '0.2rem 0.45rem', borderRadius: '4px', background: active ? '#2A4A5E' : '#EBF3F8', color: active ? '#9ECAE8' : '#5A8AA0', border: `1px solid ${active ? '#2A4A5E' : '#B8CED8'}`, fontWeight: active ? '700' : '400' }}>
                        {lbl} → {p} pt{p !== 1 ? 's' : ''}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', alignItems: 'center' }}>
              <button onClick={saveNow} style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.35rem 0.75rem', background: 'transparent',
                color: savedOk ? '#8D9A2E' : '#7A6A5A',
                border: `1px solid ${savedOk ? '#8D9A2E' : '#DDD5C8'}`,
                borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem',
                fontFamily: "'DM Sans', sans-serif"
              }}>
                <FloppyDisk size={14} />{savedOk ? '✓ Guardado' : 'Guardar datos'}
              </button>
              {!sandbox && (
                <button onClick={() => onSave({ nivelGlobal, levelKey, pts, buildingType, sistemaAguas, zonaPluviometrica, inodoros, grifos, grifosCocina, duchas, baneras, urinCist, urinPres, niveles, especificado, mediaInodoros: mediaIno, mediaGrifos: mediaGrif, mediaDuchas: mediaDuch, mediaBaneras: mediaBan })}
                  disabled={saving}
                  style={{ background: '#1A3A4E', color: '#F5F0EA', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.775rem', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: "'DM Sans', sans-serif" }}>
                  <FloppyDisk size={14} />
                  {saving ? 'Guardando…' : `Guardar puntuación AG 1 (${pts} pts)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
